import React, {useEffect, useState} from "react";
import {
    Button,
    Form,
    Input,
    Table,
    Select,
    DatePicker,
    TimePicker,
    Space,
    Divider,
    message,
    Modal,
    Popconfirm,
} from "antd";
import api from "../../api";
import dayjs from "dayjs";
import debounce from 'lodash/debounce';

const {Option} = Select;

interface Car {
    id: number;
    name: string;
    license_plate?: string;
    notes?: string;
}

interface User {
    id: number;
    username: string;
    phone: string;
}

interface Customer {
    id: number;
    name: string;
    mmid: string;
}

interface Reservation {
    id: number;
    car: Car;
    driver: User;
    customer: Customer;
    date: string;
    time_period: string;
    pickup_time: string;
    pickup_location: string;
    destination: string;
    expected_return_time: string;
    actual_return_time: string;
    notes: string;
}

interface EnhancedReservation extends Reservation {
    isPastOrReturned: boolean;
}

const DaiaCarsPage: React.FC = () => {
    const [cars, setCars] = useState<Car[]>([]);
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [customerOptions, setCustomerOptions] = useState<Customer[]>([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [editingCar, setEditingCar] = useState<Car | null>(null);
    const [loadingCars, setLoadingCars] = useState<boolean>(false);
    const [loadingRes, setLoadingRes] = useState<boolean>(false);
    const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
    const [carModalVisible, setCarModalVisible] = useState(false);
    const [reservationModalVisible, setReservationModalVisible] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const [carForm] = Form.useForm();
    const [reservationForm] = Form.useForm();

    useEffect(() => {
        fetchAll();
    }, []);

    useEffect(() => {
        if (carModalVisible && editingCar) {
            carForm.setFieldsValue({
                name: editingCar.name || "",
                license_plate: editingCar.license_plate || "",
                notes: editingCar.notes || "",
            });
        }
    }, [carModalVisible, editingCar]);

    const fetchAll = async () => {
        setLoadingCars(true)
        setLoadingRes(true)
        try {
            const [carRes, resvRes, userRes] = await Promise.all([
                api.get("/daia/cars/"),
                api.get("/daia/reservations/"),
                api.get("/users/"),
            ]);

            const carsArray = Array.isArray(carRes.data) ? carRes.data : carRes.data.results || [];
            const usersArray = Array.isArray(userRes.data) ? userRes.data : userRes.data.results || [];

            setCars(carsArray);
            setUsers(usersArray);

            const reservationsArray = Array.isArray(resvRes.data) ? resvRes.data : resvRes.data.results || [];

            const enhanced: EnhancedReservation[] = reservationsArray.map((r: Reservation) => {
                const isReturned = r.actual_return_time && r.actual_return_time !== "Pending";
                return {
                    ...r,
                    isPastOrReturned: isReturned,
                };
            });
            const now = dayjs();
            const toDT = (r: Reservation) => {
                const t = r.pickup_time?.length === 5 ? `${r.pickup_time}:00` : r.pickup_time;
                return dayjs(`${r.date} ${t}`, "YYYY-MM-DD HH:mm:ss");
            };

        // bucket: Pending returns first
            const pending = enhanced.filter(r => !r.actual_return_time || r.actual_return_time === "Pending");

        // everything else
            const rest = enhanced.filter(r => r.actual_return_time && r.actual_return_time !== "Pending");

        // break rest down into today, future, past
            const today = rest.filter(r => dayjs(r.date).isSame(now, "day"));
            const future = rest.filter(r => dayjs(r.date).isAfter(now, "day"));
            const past = rest.filter(r => dayjs(r.date).isBefore(now, "day"));

        // sort each bucket
            pending.sort((a, b) => toDT(a).valueOf() - toDT(b).valueOf());   // earliest pickup first
            today.sort((a, b) => toDT(a).valueOf() - toDT(b).valueOf());     // earliest pickup first
            future.sort((a, b) => toDT(a).valueOf() - toDT(b).valueOf());    // soonest first
            past.sort((a, b) => toDT(b).valueOf() - toDT(a).valueOf());      // most recent first

        // final order: pending → today → future → past
            const sorted = [...pending, ...today, ...future, ...past];
            setReservations(sorted);
        } catch (err) {
            message.error("Failed to load data");
        } finally {
            setLoadingCars(false)
            setLoadingRes(false)
        }
    };

    const handleCustomerSearch = debounce(async (value: string) => {
        if (!value) return;
        setCustomerLoading(true);
        try {
            const res = await api.get("/cc/customers/", {params: {search: value}});
            setCustomerOptions(res.data.results || res.data); // depending on pagination
        } catch (err) {
            message.error("Failed to search customers");
        } finally {
            setCustomerLoading(false);
        }
    }, 300); // debounce 300ms

    const handleCarSubmit = async (values: any) => {
        try {
            if (editingCar) {
                await api.put(`/daia/cars/${editingCar.id}/`, values);
                message.success("Car updated successfully");
            } else {
                await api.post("/daia/cars/", values);
                message.success("Car added successfully");
            }
            carForm.resetFields();
            setCarModalVisible(false);
            setEditingCar(null);
            fetchAll();
        } catch (err) {
            message.error("Failed to save car");
        }
    };

    const handleReservationSubmit = async (values: any) => {
        try {
            const payload = {
                car_id: values.car,
                driver_id: values.driver,
                customer_id: values.customer,
                date: values.date ? values.date.format("YYYY-MM-DD") : null,
                pickup_time: values.pickup_time ? values.pickup_time.format("HH:mm") : null,
                expected_return_time: values.expected_return_time ? values.expected_return_time.format("HH:mm") : null,
                actual_return_time: values.actual_return_time
                    ? values.actual_return_time.format("HH:mm")
                    : null,
                pickup_location: values.pickup_location,
                destination: values.destination,
                time_period: values.time_period,
                notes: values.notes,
            };

            if (editingReservation) {
                await api.put(`/daia/reservations/${editingReservation.id}/`, payload);
                message.success("Reservation updated successfully");
            } else {
                await api.post("/daia/reservations/", payload);
                message.success("Reservation added successfully");
            }

            reservationForm.resetFields();
            setReservationModalVisible(false);
            setEditingReservation(null);
            fetchAll();
        } catch (err) {
            message.error("Failed to save reservation");
        }
    };


    const openEditCar = (car: Car) => {
        setEditingCar(car);
        setCarModalVisible(true);

        // Wait for modal to be rendered before setting form values
        setTimeout(() => {
            carForm.setFieldsValue({
                name: car.name,
                license_plate: car.license_plate,
                notes: car.notes,
            });
        }, 0);
    };

    const openEditReservation = (res: Reservation) => {
        setEditingReservation(res);
        reservationForm.setFieldsValue({
            car: res.car.id,
            driver: res.driver.id,
            customer: res.customer.id,
            date: dayjs(res.date),
            time_period: res.time_period,
            pickup_time: dayjs(res.pickup_time, "HH:mm"),
            pickup_location: res.pickup_location,
            destination: res.destination,
            expected_return_time: dayjs(res.expected_return_time, "HH:mm"),
            actual_return_time: res.actual_return_time
                ? dayjs(res.actual_return_time, "HH:mm")
                : null,
            notes: res.notes,
        });
        setReservationModalVisible(true);
    };

    return (
        <div style={{padding: 24}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
                <h2 style={{margin: 0}}>Daia Cars</h2>
                <Button type="primary" onClick={() => setCarModalVisible(true)}>
                    Add Car
                </Button>
            </div>
            <Table
                rowKey="id"
                loading={loadingCars}
                dataSource={cars}
                columns={[
                    {title: "Name", dataIndex: "name"},
                    {title: "License Plate", dataIndex: "license_plate"},
                    {title: "Notes", dataIndex: "notes"},
                    {
                        title: "Actions",
                        render: (_, record) => (
                            <Button size="small" onClick={() => openEditCar(record)}>Edit</Button>

                        ),
                    },
                ]}
                pagination={false}
                scroll={{x: 'max-content'}}
            />

            <Divider/>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 32,
                marginBottom: 12
            }}>
                <h3 style={{margin: 0}}>Car Reservations</h3>
                <Button type="primary" onClick={() => setReservationModalVisible(true)}>
                    Add Reservation
                </Button>
            </div>
            <Table
                rowKey="id"
                loading={loadingRes}
                dataSource={reservations}
                columns={[
                    {title: "Date", dataIndex: "date"},
                    {title: "Car", render: (_, r) => r.car.name},
                    {title: "Driver", render: (_, r) => r.driver.username},
                    {title: "Phone", render: (_, r) => r.driver.phone || "—"}, {
                        title: "Customer",
                        render: (_, r) => `${r.customer.name} (${r.customer.mmid})`
                    },
                    {
                        title: "Period",
                        dataIndex: "time_period",
                        render: (value: string) => {
                            if (value === "full_day") return "Full Day";
                            if (value === "morning") return "Morning";
                            if (value === "afternoon") return "Afternoon";
                            if (value === "evening") return "Evening";
                            return value;
                        },
                    },
                    {
                        title: "Pickup",
                        dataIndex: "pickup_time",
                        render: (val) => dayjs(val, "HH:mm:ss").format("HH:mm"),
                    },
                    {title: "From", dataIndex: "pickup_location"},
                    {title: "To", dataIndex: "destination"},
                    {
                        title: "Expected",
                        dataIndex: "expected_return_time",
                        render: (val) => dayjs(val, "HH:mm:ss").format("HH:mm"),
                    },
                    {
                        title: "Returned",
                        dataIndex: "actual_return_time",
                        render: (val) =>
                            val ? dayjs(val, "HH:mm:ss").format("HH:mm") : "Pending",
                    },
                    {
                        title: "Actions",
                        render: (_, record) => (
                            <Space>
                                <Button size="small" onClick={() => openEditReservation(record)}>Edit</Button>
                                <Popconfirm
                                    title="Delete this reservation?"
                                    onConfirm={async () => {
                                        try {
                                            await api.delete(`/daia/reservations/${record.id}/`);
                                            message.success("Reservation deleted");
                                            fetchAll();
                                        } catch {
                                            message.error("Failed to delete reservation");
                                        }
                                    }}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button danger size="small">Delete</Button>
                                </Popconfirm>
                            </Space>
                        ),
                    },
                ]}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                scroll={{x: 'max-content'}}
                onRow={(record) => ({
                    style: {
                        backgroundColor: (record as EnhancedReservation).isPastOrReturned ? '#d9d9d9' : '#ffffff',
                        transition: 'background-color 0.3s ease',
                    },
                })}
            />

            <Modal
                open={carModalVisible}
                onCancel={() => {
                    setCarModalVisible(false);
                    setEditingCar(null);
                    carForm.resetFields();
                }}
                onOk={() => {
                    if (carModalVisible) {
                        carForm.submit();
                    }
                }}
                title={editingCar ? "Edit Car" : "Add Car"}
            >
                <Form layout="vertical" form={carForm} onFinish={handleCarSubmit}>
                    <Form.Item name="name" label="Car Name" rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="license_plate" label="License Plate">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={2}/>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={reservationModalVisible}
                onCancel={() => {
                    setReservationModalVisible(false);
                    setEditingReservation(null);
                    reservationForm.resetFields();
                }}
                onOk={() => reservationForm.submit()}
                title={editingReservation ? "Edit Reservation" : "Add Reservation"}
                width={900}
            >
                <Form layout="vertical" form={reservationForm} onFinish={handleReservationSubmit}>
                    <Space wrap>
                        <Form.Item name="car" label="Car" rules={[{required: true}]}><Select
                            style={{width: 150}}>{cars.map(car => <Option key={car.id}
                                                                          value={car.id}>{car.name}</Option>)}</Select></Form.Item>
                        <Form.Item name="driver" label="Driver" rules={[{required: true}]}><Select
                            style={{width: 150}}>{users.map(user => <Option key={user.id}
                                                                            value={user.id}>{user.username}</Option>)}</Select></Form.Item>
                        <Form.Item name="customer" label="Customer" rules={[{required: true}]}>
                            <Select
                                showSearch
                                style={{width: 250}}
                                placeholder="Search name or MMID"
                                optionFilterProp="label"
                                filterOption={false}
                                onSearch={handleCustomerSearch}
                                loading={customerLoading}
                                notFoundContent={customerLoading ? "Loading..." : "No results"}
                            >
                                {customerOptions.map((c) => (
                                    <Option key={c.id} value={c.id} label={`${c.name} ${c.mmid}`}>
                                        {`${c.name} (${c.mmid})`}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="date" label="Date"
                                   rules={[{required: true, message: "Please enter Date"}]}><DatePicker/></Form.Item>
                        <Form.Item name="time_period" label="Period"
                                   rules={[{required: true, message: "Please enter time period"}]}><Select
                            style={{width: 120}}><Option value="morning">Morning</Option><Option
                            value="afternoon">Afternoon</Option><Option value="evening">Evening</Option><Option
                            value="full_day">Full Day</Option></Select></Form.Item>
                        <Form.Item name="pickup_time" label="Pickup Time"
                                   rules={[{required: true, message: "Please enter pickup time"}]}><TimePicker
                            format="HH:mm"/></Form.Item>
                        <Form.Item name="pickup_location" label="Pickup Location"
                                   rules={[{required: true, message: "Please enter pickup location"}]}>
                            <Input/></Form.Item>
                        <Form.Item name="destination" label="Destination"
                                   rules={[{required: true, message: "Please enter destination"}]}>
                            <Input/></Form.Item>
                        <Form.Item name="expected_return_time" label="Expected Return"
                                   rules={[{required: true, message: "Please enter expected return"}]}>
                            <TimePicker
                                format="HH:mm"/></Form.Item>
                        <Form.Item name="actual_return_time" label="Actual Return">
                            <TimePicker
                                format="HH:mm"/></Form.Item>
                        <Form.Item name="notes" label="Notes"> <Input.TextArea rows={1}/></Form.Item>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};

export default DaiaCarsPage;